import { Injectable, inject } from '@angular/core';
import {
  type CreateNotificationDTO,
  type MaintenanceNotification,
  NotificationSyncStatusEnum,
  type RejectNotificationDTO,
} from '@mymaintenance/shared';
import {
  infiniteQueryOptions,
  injectMutation,
  QueryClient,
  queryOptions,
} from '@tanstack/angular-query-experimental';
import { NotificationApi } from '../api/notification-api.service';
import { TranslateService } from '../services/i18n.service';
import { NotificationsService } from '../services/notifications.service';
import { type MutationCallbacks, writeMutation } from './query.utils';

export const NotificationKeys = {
  all: ['notifications'] as const,
  byId: (id: string) => [...NotificationKeys.all, 'detail', id] as const,
  pending: () => [...NotificationKeys.all, 'pending'] as const,
  mine: () => [...NotificationKeys.all, 'mine'] as const,
  activeAt: (functionalLocation: string) =>
    [...NotificationKeys.all, 'active', functionalLocation] as const,
  history: (functionalLocation: string, equipment: string | null) =>
    [...NotificationKeys.all, 'history', functionalLocation, equipment] as const,
} as const;

const ACTIVE_AT_LOCATION_LIMIT = 20;
const ACTIVE_AT_LOCATION_STALE_TIME_MS = 30_000;
const HISTORY_PAGE_SIZE = 25;

export interface HistoryAsset {
  readonly functionalLocation?: string;
  readonly equipment?: string;
}

export interface ApproveVariables {
  readonly id: string;
  /** The corrected notification, when the validator changed something. */
  readonly correction?: CreateNotificationDTO;
}

export interface RejectVariables {
  readonly id: string;
  readonly rejection: RejectNotificationDTO;
}

@Injectable({ providedIn: 'root' })
export class NotificationQueryService {
  readonly #api = inject(NotificationApi);
  readonly #queryClient = inject(QueryClient);
  readonly #notifications = inject(NotificationsService);
  readonly #translate = inject(TranslateService);

  readonly queries = {
    byId: (id: string) =>
      queryOptions({
        queryKey: NotificationKeys.byId(id),
        queryFn: () => this.#api.get(id),
      }),
    activeAt: (functionalLocation: string | undefined) =>
      queryOptions({
        queryKey: NotificationKeys.activeAt(functionalLocation ?? ''),
        queryFn: async () =>
          (
            await this.#api.list({
              functionalLocation,
              active: true,
              limit: ACTIVE_AT_LOCATION_LIMIT,
            })
          ).items,
        enabled: !!functionalLocation,
        staleTime: ACTIVE_AT_LOCATION_STALE_TIME_MS,
      }),
    history: ({ functionalLocation, equipment }: HistoryAsset) =>
      infiniteQueryOptions({
        queryKey: NotificationKeys.history(functionalLocation ?? '', equipment ?? null),
        queryFn: ({ pageParam }) =>
          this.#api.list({
            functionalLocation,
            equipment,
            limit: HISTORY_PAGE_SIZE,
            cursor: pageParam,
          }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.cursor,
        enabled: !!functionalLocation,
      }),
    pending: () =>
      queryOptions({
        queryKey: NotificationKeys.pending(),
        queryFn: () => this.#api.listAll({ syncStatus: NotificationSyncStatusEnum.PENDING }),
      }),
    /** Everything the signed-in user created, whatever its status. */
    mine: () =>
      queryOptions({
        queryKey: NotificationKeys.mine(),
        queryFn: () => this.#api.listAll({ mine: true }),
      }),
  };

  readonly mutations = {
    create: (
      callbacks?: MutationCallbacks<MaintenanceNotification, Error, CreateNotificationDTO>,
    ) =>
      injectMutation(() => ({
        mutationKey: [...NotificationKeys.all, 'create'],
        mutationFn: (notification: CreateNotificationDTO) => this.#api.create(notification),
        onSuccess: async (data: MaintenanceNotification, variables: CreateNotificationDTO) => {
          await this.#queryClient.invalidateQueries({ queryKey: NotificationKeys.all });
          callbacks?.onSuccess?.(data, variables);
        },
        onError: callbacks?.onError,
      })),
    approve: (callbacks?: MutationCallbacks<MaintenanceNotification, Error, ApproveVariables>) =>
      writeMutation(
        this.#queryClient,
        this.#notifications,
        NotificationKeys.all,
        this.#translate.getKeyTranslation('validateNotice.toasts.approveFailed'),
        {
          key: 'approve',
          mutationFn: ({ id, correction }: ApproveVariables) => this.#api.approve(id, correction),
          successMessage: (notification) =>
            this.#translate.getKeyTranslation('validateNotice.toasts.approved', [
              notification.ID,
            ])(),
          callbacks,
        },
      ),
    reject: (callbacks?: MutationCallbacks<MaintenanceNotification, Error, RejectVariables>) =>
      writeMutation(
        this.#queryClient,
        this.#notifications,
        NotificationKeys.all,
        this.#translate.getKeyTranslation('validateNotice.toasts.rejectFailed'),
        {
          key: 'reject',
          mutationFn: ({ id, rejection }: RejectVariables) => this.#api.reject(id, rejection),
          successMessage: (notification) =>
            this.#translate.getKeyTranslation('validateNotice.toasts.rejected', [
              notification.REPORTEDBYUSER,
            ])(),
          callbacks,
        },
      ),
  };
}
